import { useRef, SyntheticEvent, useCallback, useState } from 'react'
import { AppManager } from '@app/managers'
import { useRestWebsiteContext } from '@app/components/providers/rest/rest-website'
import { AccessibilityStandardKeys } from '@app/components/general/select/select-input'
import { classNames } from '@app/utils/classes'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { twMerge } from 'tailwind-merge'

const CtaInputRest = ({
  small,
  standard,
  runners,
}: {
  small?: boolean
  standard?: AccessibilityStandardKeys
  runners?: string[]
}) => {
  const { t, lang } = useTranslation()
  const [pdfView, setPDFView] = useState<boolean>(false)

  const ref = useRef<HTMLInputElement>(null)
  const {
    search,
    setSearch,
    loading,
    toggleModal,
    onPdfScanEvent,
    onFileChange,
    selectedFile,
  } = useRestWebsiteContext()

  const submitForm = useCallback(
    async (e: SyntheticEvent<HTMLFormElement>) => {
      e?.preventDefault()

      if (!search) {
        return AppManager.toggleSnack(true, t('enter-valid-url'), 'error', true)
      }

      await toggleModal({ query: search, standard, lang, runners })

      if (ref.current) {
        ref.current.value = ''
      }
    },
    [toggleModal, ref, search, standard, lang, runners, t]
  )

  const onChangePDFView = () => setPDFView((x) => !x)

  const disableSubmit = pdfView ? !selectedFile : !search

  const disabledBtn = loading || disableSubmit

  const inputCSS = twMerge(
    small ? 'py-1.5' : 'py-3',
    !search
      ? 'dark:hover:bg-gray-800 hover:bg-[rgb(203,203,203)]'
      : 'dark:hover:bg-gray-700 hover:bg-[rgb(206,206,206)]',
    `w-full min-w-[180px] md:min-w-[420px] px-6 rounded-sm text-lg bg-gray-300 text-gray-900 hover:ring dark:bg-transparent dark:text-white`,
    'md:w-inherit border-2 dark:border-gray-500'
  )

  const submitStyles = classNames(
    disableSubmit
      ? 'text-gray-600 bg-gray-300 dark:bg-gray-50'
      : 'text-gray-50 bg-black dark:text-gray-800 hover:bg-gray-700 dark:hover:text-gray-900 dark:hover:bg-gray-300 dark:bg-gray-200',
    `min-w-[187.562px] py-1.5 text-xl font-bold rounded-sm`
  )

  return (
    <>
      <form onSubmit={pdfView ? onPdfScanEvent : submitForm} noValidate>
        <div className='pb-3'>
          <input
            type={'checkbox'}
            onChange={onChangePDFView}
            checked={pdfView}
            id='pdf-view'
            placeholder='PDF'
          />
          <label htmlFor='pdf-view' className='px-2 uppercase'>
            {t('audit-form:pdf')}
          </label>
        </div>

        <div className={`flex flex-col md:flex-row gap-1 w-full md:w-fit`}>
          <div
            className={!pdfView ? 'hidden' : undefined}
            aria-hidden={!pdfView ? true : undefined}
          >
            <label htmlFor='search-pdf-c' className='sr-only'>
              {t('audit-form:upload-pdf')}
            </label>
            <input
              placeholder={'PDF'}
              id='search-pdf-c'
              ref={ref}
              type='file'
              className={classNames(inputCSS, 'decoration-none')}
              onChange={onFileChange}
              accept={'application/pdf'}
              name={'pdf'}
            />
          </div>
          <div
            className={pdfView ? 'hidden' : undefined}
            aria-hidden={pdfView ? true : undefined}
          >
            <label htmlFor='search-input-c' className='sr-only'>
              {t('common:enter-label')}
            </label>
            <input
              placeholder={t('common:enter-url')}
              id='search-input-c'
              ref={ref}
              type='url'
              value={search || ''}
              className={classNames(inputCSS, 'min-h-[59px]')}
              onChange={setSearch}
              minLength={4}
              name={'search'}
            />
          </div>
          <button
            className={submitStyles}
            type={'submit'}
            disabled={disabledBtn}
          >
            {t('common:analyze')}
          </button>
        </div>
      </form>
    </>
  )
}

export { CtaInputRest }
